<script lang="ts">
  import { onMount, onDestroy, tick } from 'svelte';
  import { zoom as d3zoom, zoomIdentity, type ZoomBehavior } from 'd3-zoom';
  import { select } from 'd3-selection';
  import {
    currentPeople, currentRels, currentTree, selectedPersonId,
    currentTreeId, createPerson, deletePerson, linkParent, linkSpouse, updateTree,
  } from '../lib/stores';
  import { layout, type LayoutResult } from '../lib/tree-layout';
  import { isLiving } from '../lib/living';

  let svg: SVGSVGElement;
  let g: SVGGElement;
  let wrap: HTMLDivElement;
  let zoomBehavior: ZoomBehavior<SVGSVGElement, unknown>;
  let data: LayoutResult = { nodes: [], links: [], orphanBand: null, width: 0, height: 0 };
  let transform = zoomIdentity;

  // ---------- Right-click context menu ----------
  type Menu =
    | null
    | { kind: 'node'; x: number; y: number; nodeId: string }
    | { kind: 'canvas'; x: number; y: number; svgX: number; svgY: number };
  let menu: Menu = null;

  $: data = layout($currentPeople, $currentRels, $currentTree?.rootPersonId);

  function path(source: {x:number;y:number}, target: {x:number;y:number}): string {
    const mid = (source.y + target.y) / 2;
    return `M ${source.x + 90} ${source.y + 80}
            V ${mid}
            H ${target.x + 90}
            V ${target.y}`;
  }

  function spousePath(source: {x:number;y:number}, target: {x:number;y:number}): string {
    return `M ${source.x + 180} ${source.y + 40} H ${target.x}`;
  }

  onMount(() => {
    zoomBehavior = d3zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 3])
      .on('zoom', (ev) => {
        transform = ev.transform;
        select(g).attr('transform', ev.transform.toString());
      });
    select(svg).call(zoomBehavior);
    fit();
    window.addEventListener('keydown', onKey);
    window.addEventListener('click', closeMenuOnOutside, true);
  });

  onDestroy(() => {
    if (svg) select(svg).on('.zoom', null);
    window.removeEventListener('keydown', onKey);
    window.removeEventListener('click', closeMenuOnOutside, true);
  });

  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') menu = null;
  }
  function closeMenuOnOutside(e: MouseEvent) {
    if (!menu) return;
    const t = e.target as Element | null;
    if (t && t.closest('.ctx-menu')) return;
    menu = null;
  }

  export function fit() {
    if (!svg || !data.width) return;
    const rect = svg.getBoundingClientRect();
    const scale = Math.min(
      (rect.width - 60) / Math.max(1, data.width),
      (rect.height - 60) / Math.max(1, data.height),
      1,
    );
    const tx = (rect.width - data.width * scale) / 2;
    const ty = 30;
    select(svg).transition().duration(280).call(
      zoomBehavior.transform,
      zoomIdentity.translate(tx, ty).scale(scale),
    );
  }

  function zoomBy(factor: number) {
    select(svg).transition().duration(180).call(zoomBehavior.scaleBy, factor);
  }

  function select_(id: string) { selectedPersonId.set(id); }

  function initials(name: string): string {
    return name.trim().split(/\s+/).map((s) => s[0]).slice(0, 2).join('').toUpperCase() || '—';
  }

  function genderTint(g?: string): string {
    if (g === 'female') return 'url(#gradF)';
    if (g === 'male')   return 'url(#gradM)';
    return 'url(#gradN)';
  }

  function onNodeContext(ev: MouseEvent, nodeId: string) {
    ev.preventDefault();
    ev.stopPropagation();
    selectedPersonId.set(nodeId);
    menu = { kind: 'node', x: ev.clientX, y: ev.clientY, nodeId };
  }

  function onCanvasContext(ev: MouseEvent) {
    ev.preventDefault();
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    // Convert client → svg-local (in zoomed space)
    const localX = (ev.clientX - rect.left - transform.x) / transform.k;
    const localY = (ev.clientY - rect.top - transform.y) / transform.k;
    menu = { kind: 'canvas', x: ev.clientX, y: ev.clientY, svgX: localX, svgY: localY };
  }

  // ---------- Quick-add actions (used by both context menu + inline buttons) ----------

  async function addRelative(targetId: string, kind: 'parent' | 'child' | 'spouse') {
    const treeId = $currentTreeId;
    if (!treeId) return;
    const np = await createPerson(treeId, { name: 'New person' });
    if (kind === 'parent') await linkParent(np.id, targetId);
    if (kind === 'child')  await linkParent(targetId, np.id);
    if (kind === 'spouse') await linkSpouse(targetId, np.id);
    selectedPersonId.set(np.id);
    menu = null;
  }

  async function addPersonAtPoint() {
    const treeId = $currentTreeId;
    if (!treeId) return;
    const np = await createPerson(treeId, { name: 'New person' });
    selectedPersonId.set(np.id);
    menu = null;
    await tick();
  }

  async function setAsRoot(personId: string) {
    if (!$currentTree) return;
    await updateTree($currentTree.id, { rootPersonId: personId });
    menu = null;
    await tick();
    fit();
  }

  async function adoptOrphan(orphanId: string) {
    // Convenience: link selected (or first non-orphan) as the orphan's parent.
    const sel = $selectedPersonId;
    const target = sel && sel !== orphanId ? sel : data.nodes.find((n) => !n.orphan && n.id !== orphanId)?.id;
    if (!target) return;
    await linkParent(target, orphanId);
    menu = null;
  }

  async function removePerson(id: string) {
    const p = $currentPeople.find((x) => x.id === id);
    if (!p) return;
    if (!confirm(`Delete ${p.name}? All relationships are removed.`)) return;
    await deletePerson(id);
    if ($selectedPersonId === id) selectedPersonId.set(null);
    menu = null;
  }

  $: selectedNode = $selectedPersonId ? data.nodes.find((n) => n.id === $selectedPersonId) : null;
  $: empty = $currentPeople.length === 0;
</script>

<div class="canvas-wrap" bind:this={wrap}>
  <div class="toolbar">
    <button class="ghost small" on:click={fit} title="Fit to screen">fit</button>
    <button class="ghost small" on:click={() => zoomBy(1.2)} aria-label="Zoom in">＋</button>
    <button class="ghost small" on:click={() => zoomBy(1/1.2)} aria-label="Zoom out">−</button>
    <span class="sep"></span>
    <span class="chip">{$currentPeople.length} nodes</span>
    <span class="chip">{$currentRels.length} links</span>
    <span class="chip dim">{Math.round(transform.k * 100)}%</span>
    {#if data.orphanBand}
      <span class="sep"></span>
      <span class="chip warn-chip" title="People with no parent, child, or spouse links yet">
        {data.orphanBand.count} unlinked
      </span>
    {/if}
  </div>

  <!-- Empty-state coachmark -->
  {#if empty}
    <div class="coach">
      <div class="coach-card">
        <h3>Empty tree.</h3>
        <p class="dim">
          Two ways to start:
        </p>
        <ul class="actions">
          <li>
            <button class="primary" on:click={addPersonAtPoint}>+ add first person</button>
            <span class="dim small">→ then right-click them to add parents, children, a spouse.</span>
          </li>
          <li>
            <span class="dim small">or import a GEDCOM from the toolbar above (↓ import).</span>
          </li>
        </ul>
        <p class="hint mono dim small">Tip: right-click anywhere on the canvas to add a person at that spot.</p>
      </div>
    </div>
  {/if}

  <!-- canvas dot-grid background -->
  <div class="grid" aria-hidden="true"></div>

  <svg bind:this={svg} class="canvas" role="application" aria-label="Family tree"
       on:contextmenu={onCanvasContext}>
    <defs>
      <linearGradient id="gradF" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#1a241e"/>
        <stop offset="1" stop-color="#23332a"/>
      </linearGradient>
      <linearGradient id="gradM" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#1a241e"/>
        <stop offset="1" stop-color="#1e2a3a"/>
      </linearGradient>
      <linearGradient id="gradN" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#1a241e"/>
        <stop offset="1" stop-color="#2a2420"/>
      </linearGradient>
      <linearGradient id="avatar" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#d8a74a"/>
        <stop offset="1" stop-color="#6eb29c"/>
      </linearGradient>
    </defs>
    <g bind:this={g}>
      <!-- Orphan band background + label -->
      {#if data.orphanBand}
        <g class="orphan-band">
          <rect x={data.orphanBand.x - 16} y={data.orphanBand.y - 38}
                width={data.orphanBand.width + 32}
                height={data.orphanBand.height + 56}
                rx="14" />
          <text x={data.orphanBand.x - 4} y={data.orphanBand.y - 16} class="band-label mono">
            unlinked · {data.orphanBand.count} {data.orphanBand.count === 1 ? 'person' : 'people'} · right-click to connect
          </text>
        </g>
      {/if}

      {#each data.links as l, i}
        {#if l.kind === 'parent'}
          <path class="link parent" d={path(l.source, l.target)} />
        {:else}
          <path class="link spouse" d={spousePath(l.source, l.target)} />
        {/if}
      {/each}
      {#each data.nodes as n (n.id)}
        <g class="node {$selectedPersonId === n.id ? 'selected' : ''} {n.orphan ? 'orphan' : ''}"
           transform={`translate(${n.x},${n.y})`}
           on:click={() => select_(n.id)}
           on:contextmenu={(e) => onNodeContext(e, n.id)}
           role="button"
           tabindex="0"
           aria-label={`${n.person.name}${n.orphan ? ' (unlinked)' : ''} — right-click for actions`}
           on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && select_(n.id)}>
          <rect width="180" height="80" rx="10" fill={genderTint(n.person.gender)} stroke="var(--ls-border)" stroke-width="1"/>
          <!-- top accent bar -->
          <rect x="0" y="0" width="180" height="3" rx="10" fill="url(#avatar)" opacity={$selectedPersonId === n.id ? 1 : 0.35}/>
          <!-- avatar circle with initials -->
          <circle cx="22" cy="40" r="14" fill="url(#avatar)" opacity=".92"/>
          <text class="initials" x="22" y="44" text-anchor="middle">{initials(n.person.name)}</text>
          <text class="name" x="44" y="30">{(n.person.label || n.person.name || '—').slice(0, 24)}</text>
          <text class="sub mono" x="44" y="46">
            {[n.person.dob ?? '', n.person.death ? `—${n.person.death}` : ''].filter(Boolean).join(' ') || 'dates unknown'}
          </text>
          <text class="sub mono" x="44" y="62">
            {n.person.gender ?? ''}{isLiving(n.person) ? ' · living' : ''}
          </text>
          <!-- orphan badge -->
          {#if n.orphan}
            <rect class="orphan-badge" x="120" y="6" width="54" height="14" rx="7"/>
            <text class="orphan-badge-text mono" x="147" y="16" text-anchor="middle">unlinked</text>
          {/if}
          <!-- id chip -->
          <text class="idchip mono" x="172" y="74" text-anchor="end">{n.id.slice(0, 6)}</text>

          <!-- Inline relative buttons, only on the selected node -->
          {#if $selectedPersonId === n.id}
            <g class="quick-adds" pointer-events="all">
              <!-- parent (above) -->
              <g class="qa" role="button" tabindex="0" aria-label="Add parent"
                 on:click|stopPropagation={() => addRelative(n.id, 'parent')}
                 on:keydown|stopPropagation={(e) => (e.key === 'Enter' || e.key === ' ') && addRelative(n.id, 'parent')}>
                <circle cx="90" cy="-12" r="11"/>
                <text x="90" y="-8" text-anchor="middle">+</text>
                <text class="qa-cap mono" x="90" y="-28" text-anchor="middle">parent</text>
              </g>
              <!-- child (below) -->
              <g class="qa" role="button" tabindex="0" aria-label="Add child"
                 on:click|stopPropagation={() => addRelative(n.id, 'child')}
                 on:keydown|stopPropagation={(e) => (e.key === 'Enter' || e.key === ' ') && addRelative(n.id, 'child')}>
                <circle cx="90" cy="92" r="11"/>
                <text x="90" y="96" text-anchor="middle">+</text>
                <text class="qa-cap mono" x="90" y="112" text-anchor="middle">child</text>
              </g>
              <!-- spouse (right) -->
              <g class="qa" role="button" tabindex="0" aria-label="Add spouse"
                 on:click|stopPropagation={() => addRelative(n.id, 'spouse')}
                 on:keydown|stopPropagation={(e) => (e.key === 'Enter' || e.key === ' ') && addRelative(n.id, 'spouse')}>
                <circle cx="192" cy="40" r="11"/>
                <text x="192" y="44" text-anchor="middle">+</text>
                <text class="qa-cap mono" x="192" y="60" text-anchor="middle">spouse</text>
              </g>
            </g>
          {/if}
        </g>
      {/each}
    </g>
  </svg>

  <!-- Right-click context menu -->
  {#if menu && menu.kind === 'node'}
    {@const m = menu}
    {@const node = data.nodes.find((n) => n.id === m.nodeId)}
    <div class="ctx-menu" style={`left: ${m.x}px; top: ${m.y}px`}>
      <div class="ctx-head mono">
        {node?.person.name ?? '—'}
      </div>
      <button on:click={() => addRelative(m.nodeId, 'parent')}>＋ Add parent</button>
      <button on:click={() => addRelative(m.nodeId, 'child')}>＋ Add child</button>
      <button on:click={() => addRelative(m.nodeId, 'spouse')}>＋ Add spouse</button>
      <div class="ctx-sep"></div>
      <button on:click={() => { selectedPersonId.set(m.nodeId); menu = null; }}>✎ Edit details</button>
      {#if node?.orphan}
        <button on:click={() => adoptOrphan(m.nodeId)} title="Link as child of the currently-selected person (or the first node in the main tree)">
          ⮕ Link to selected as child
        </button>
      {/if}
      <button on:click={() => setAsRoot(m.nodeId)}>★ Set as tree root</button>
      <div class="ctx-sep"></div>
      <button class="danger" on:click={() => removePerson(m.nodeId)}>✕ Delete</button>
    </div>
  {:else if menu && menu.kind === 'canvas'}
    {@const m = menu}
    <div class="ctx-menu" style={`left: ${m.x}px; top: ${m.y}px`}>
      <div class="ctx-head mono">empty canvas</div>
      <button on:click={addPersonAtPoint}>＋ Add person here</button>
    </div>
  {/if}
</div>

<style>
  .canvas-wrap {
    position: relative;
    width: 100%;
    height: 100%;
    background: var(--ls-bg);
    overflow: hidden;
  }
  .grid {
    position: absolute; inset: 0;
    background-image:
      radial-gradient(rgba(255,255,255,.04) 1px, transparent 1px);
    background-size: 24px 24px;
    pointer-events: none;
  }
  .toolbar {
    position: absolute; top: 12px; left: 12px; z-index: 2;
    display: flex; gap: 6px; align-items: center;
    background: color-mix(in srgb, var(--ls-surface) 92%, transparent);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid var(--ls-border);
    border-radius: var(--ls-radius);
    padding: 4px 6px;
    box-shadow: var(--ls-shadow);
  }
  .sep { width: 1px; height: 18px; background: var(--ls-border); margin: 0 4px; }
  .chip.warn-chip {
    color: var(--ls-warn, #d8a74a);
    background: color-mix(in srgb, var(--ls-warn, #d8a74a) 14%, transparent);
    border-color: color-mix(in srgb, var(--ls-warn, #d8a74a) 40%, transparent);
  }
  .canvas { width: 100%; height: 100%; display: block; touch-action: none; cursor: grab; }
  .canvas:active { cursor: grabbing; }
  .node { transition: filter .15s var(--ls-ease); cursor: pointer; }
  .node:hover { filter: drop-shadow(0 4px 14px rgba(245,177,76,.25)); }
  .node.selected { filter: drop-shadow(0 4px 18px rgba(245,177,76,.35)); }
  .node:hover rect:first-of-type { stroke: color-mix(in srgb, var(--ls-signal) 60%, var(--ls-border)); }
  .node.selected rect:first-of-type { stroke: var(--ls-signal); stroke-width: 1.5; }
  .node.orphan rect:first-of-type {
    stroke-dasharray: 5 4;
    stroke: color-mix(in srgb, var(--ls-warn, #d8a74a) 60%, var(--ls-border));
  }
  .node text { fill: var(--ls-text); pointer-events: none; font-family: var(--ls-font); }
  .node .name { font-weight: 600; font-size: 13px; fill: var(--ls-ink); }
  .node .sub { font-size: 10px; fill: var(--ls-text-muted); }
  .node .initials { font-size: 10px; font-weight: 700; fill: #0a100d; font-family: var(--ls-font-mono); }
  .node .idchip { font-size: 9px; fill: var(--ls-text-dim); font-family: var(--ls-font-mono); }
  .node .orphan-badge {
    fill: color-mix(in srgb, var(--ls-warn, #d8a74a) 22%, transparent);
    stroke: color-mix(in srgb, var(--ls-warn, #d8a74a) 50%, transparent);
    stroke-width: 1;
  }
  .node .orphan-badge-text {
    font-size: 9px;
    fill: var(--ls-warn, #d8a74a);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .04em;
  }
  .link { fill: none; stroke: var(--ls-forest-2); stroke-width: 1.2; opacity: .65; }
  .link.spouse { stroke-dasharray: 4 3; stroke: var(--ls-accent); opacity: .55; }

  /* Orphan band rectangle behind the unlinked grid */
  .orphan-band rect {
    fill: color-mix(in srgb, var(--ls-warn, #d8a74a) 5%, transparent);
    stroke: color-mix(in srgb, var(--ls-warn, #d8a74a) 22%, transparent);
    stroke-width: 1;
    stroke-dasharray: 6 4;
  }
  .orphan-band .band-label {
    fill: var(--ls-warn, #d8a74a);
    font-size: 11px;
    letter-spacing: .04em;
    text-transform: uppercase;
    font-weight: 600;
    opacity: .85;
  }

  /* Inline quick-add affordances on the selected node */
  .quick-adds .qa { cursor: pointer; }
  .quick-adds .qa circle {
    fill: var(--ls-signal);
    stroke: #1a120a;
    stroke-width: 1;
    transition: transform .12s var(--ls-ease);
    transform-origin: center;
  }
  .quick-adds .qa:hover circle { fill: var(--ls-signal-2); }
  .quick-adds .qa text {
    fill: #1a120a;
    font-weight: 700;
    font-size: 14px;
    pointer-events: none;
  }
  .quick-adds .qa .qa-cap {
    font-size: 10px;
    font-weight: 500;
    fill: var(--ls-text-muted);
    text-transform: uppercase;
    letter-spacing: .06em;
  }

  /* Context menu */
  .ctx-menu {
    position: fixed;
    z-index: 50;
    min-width: 200px;
    padding: 6px;
    background: var(--ls-surface);
    border: 1px solid var(--ls-border);
    border-radius: var(--ls-radius);
    box-shadow: var(--ls-shadow-lg);
    display: flex; flex-direction: column; gap: 2px;
  }
  .ctx-menu .ctx-head {
    font-size: 11px;
    color: var(--ls-text-dim);
    padding: 6px 10px 4px;
    border-bottom: 1px solid var(--ls-border);
    margin-bottom: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ctx-menu button {
    background: transparent;
    border: none;
    box-shadow: none;
    text-align: left;
    padding: 7px 10px;
    border-radius: 6px;
    font-size: 13px;
    color: var(--ls-text);
    cursor: pointer;
  }
  .ctx-menu button:hover { background: var(--ls-surface-2); }
  .ctx-menu button.danger { color: var(--ls-danger); }
  .ctx-menu .ctx-sep { height: 1px; background: var(--ls-border); margin: 4px 2px; }

  /* Empty-state coachmark */
  .coach {
    position: absolute; inset: 0;
    display: grid; place-items: center;
    pointer-events: none;
    z-index: 3;
  }
  .coach-card {
    pointer-events: auto;
    max-width: 460px;
    padding: 22px 24px;
    background: color-mix(in srgb, var(--ls-surface) 96%, transparent);
    border: 1px solid var(--ls-border);
    border-radius: var(--ls-radius-lg);
    box-shadow: var(--ls-shadow-lg);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }
  .coach-card h3 { margin: 0 0 10px; font-size: var(--ls-step-3); }
  .coach-card .actions { list-style: none; padding: 0; margin: 12px 0; display: flex; flex-direction: column; gap: 10px; }
  .coach-card .actions li { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
  .coach-card .hint { margin: 14px 0 0; }
</style>
